<template>
    <div class="cert-page">
        <div class="row">
            <div class="col-lg-8">
                <!-- Domains -->
                <div class="card" :class="{ 'card-editing': isEditing('domains') }">
                    <div class="card-header cert-card-header">
                        <h5>Domains</h5>
                        <span v-if="isEditing('domains')" class="badge-soft bs-info ms-auto">Editing</span>
                        <div v-else class="cert-card-actions">
                            <button type="button" class="btn btn-sm btn-light" @click="copyDomains"
                                :disabled="!certificate">{{
                                    copiedDomains ? 'Copied' : 'Copy all' }}</button>
                            <button type="button" class="btn btn-sm btn-outline-primary" @click="startEdit('domains')"
                                :disabled="!certificate">Edit</button>
                        </div>
                    </div>
                    <div class="card-body" v-if="certificate">
                        <template v-if="isEditing('domains')">
                            <input-field label="Common name" type="text" v-model="certificate.commonName"
                                :editable="false" :toolTip="'Certificate name'" />
                            <p class="field-hint">Fixed for the life of the certificate.</p>
                            <input-field label="Subject alternative names" type="multiselect" v-model="certificate.sans"
                                :validate="getValidation('Sans')" :editable="true"
                                :toolTip="'Subject alternative names'" placeholder="Type a hostname and press enter" />
                        </template>
                        <template v-else>
                            <dl class="pairs pairs-wide">
                                <div>
                                    <dt>
                                        Common name
                                        <i class="fa fa-circle-info text-muted" title="Certificate name"
                                            data-bs-toggle="tooltip" data-bs-placement="right"></i>
                                    </dt>
                                    <dd>{{ certificate.commonName || '—' }}</dd>
                                </div>
                                <div>
                                    <dt>
                                        SANs
                                        <i class="fa fa-circle-info text-muted" title="Subject alternative names"
                                            data-bs-toggle="tooltip" data-bs-placement="right"></i>
                                    </dt>
                                    <dd>
                                        <div class="chips" v-if="sanList.length">
                                            <span class="chip" v-for="(san, index) in sanList"
                                                :key="`${san}-${index}`">{{ san }}</span>
                                        </div>
                                        <span v-else>—</span>
                                    </dd>
                                </div>
                            </dl>
                        </template>
                    </div>
                    <div class="card-body" v-else>
                        <div class="sk" style="width:45%"></div>
                        <div class="sk mt-2" style="width:70%"></div>
                    </div>
                </div>

                <div class="row">
                    <!-- Admin contact -->
                    <div class="col-md-6">
                        <div class="card" :class="{ 'card-editing': isEditing('admin') }">
                            <div class="card-header cert-card-header">
                                <h5>Admin contact</h5>
                                <span v-if="isEditing('admin')" class="badge-soft bs-info ms-auto">Editing</span>
                                <button v-else type="button" class="btn btn-sm btn-outline-primary ms-auto"
                                    @click="startEdit('admin')" :disabled="!certificate?.admin">Edit</button>
                            </div>
                            <div class="card-body" v-if="certificate?.admin">
                                <template v-if="isEditing('admin')">
                                    <div class="row g-3">
                                        <div class="col-sm-6">
                                            <input-field label="First name" type="text" :toolTip="`Admin's first name`"
                                                v-model="certificate.admin.firstName"
                                                :validate="getValidation('First name')" :editable="true" />
                                        </div>
                                        <div class="col-sm-6">
                                            <input-field label="Last name" type="text" :toolTip="`Admin's last name`"
                                                v-model="certificate.admin.lastName"
                                                :validate="getValidation('Last name')" :editable="true" />
                                        </div>
                                        <div class="col-12">
                                            <input-field label="Email" type="text" v-model="certificate.admin.email"
                                                placeholder="aut@aqfer.com" :validate="emailValidation"
                                                :toolTip="`Admin's email`" :editable="true" />
                                        </div>
                                        <div class="col-12">
                                            <input-field label="Phone" type="text" v-model="certificate.admin.phone"
                                                :validate="getValidation('Phone')" :editable="true"
                                                :toolTip="`Admin's phone`" />
                                        </div>
                                    </div>
                                </template>
                                <template v-else>
                                    <dl class="pairs">
                                        <div>
                                            <dt>
                                                Name
                                                <i class="fa fa-circle-info text-muted" title="Admin's name"
                                                    data-bs-toggle="tooltip" data-bs-placement="right"></i>
                                            </dt>
                                            <dd>{{ adminName || '—' }}</dd>
                                        </div>
                                        <div>
                                            <dt>
                                                Email
                                                <i class="fa fa-circle-info text-muted" title="Admin's email"
                                                    data-bs-toggle="tooltip" data-bs-placement="right"></i>
                                            </dt>
                                            <dd>{{ certificate.admin.email || '—' }}</dd>
                                        </div>
                                        <div>
                                            <dt>
                                                Phone
                                                <i class="fa fa-circle-info text-muted" title="Admin's phone"
                                                    data-bs-toggle="tooltip" data-bs-placement="right"></i>
                                            </dt>
                                            <dd>{{ certificate.admin.phone || '—' }}</dd>
                                        </div>
                                    </dl>
                                </template>
                            </div>
                            <div class="card-body" v-else>
                                <div class="sk" style="width:55%"></div>
                                <div class="sk mt-2" style="width:75%"></div>
                                <div class="sk mt-2" style="width:40%"></div>
                            </div>
                        </div>

                        <!-- Certificate information -->
                        <div class="card" :class="{ 'card-editing': isEditing('subject') }">
                            <div class="card-header cert-card-header">
                                <h5>Certificate information</h5>
                                <template v-if="isEditing('subject')">
                                    <button type="button" class="btn btn-sm btn-outline-primary ms-auto"
                                        @click="copyFromOrg">
                                        Copy from organization
                                    </button>
                                </template>
                                <template v-else-if="certificate?.certificateInfo">
                                    <div class="cert-card-actions">
                                        <span class="badge-soft" :class="subjectMatchesOrg ? 'bs-ok' : 'bs-warn'">
                                            {{ subjectMatchesOrg ? 'Matches organization' : 'Differs from organization'
                                            }}
                                        </span>
                                        <button type="button" class="btn btn-sm btn-outline-primary"
                                            @click="startEdit('subject')">Edit</button>
                                    </div>
                                </template>
                            </div>
                            <div class="card-body" v-if="certificate?.certificateInfo">
                                <div class="row g-3" v-if="isEditing('subject')">
                                    <div class="col-md-6">
                                        <input-field label="Organization name" type="text"
                                            v-model="certificate.certificateInfo.organizationName"
                                            :validate="getValidation(`Organization name`)" :editable="true" />
                                    </div>
                                    <div class="col-md-6">
                                        <input-field label="Country" type="text"
                                            v-model="certificate.certificateInfo.country"
                                            :validate="getValidation('Country')" :editable="true"
                                            :toolTip="'Two-letter ISO country code, for example US'" />
                                    </div>
                                    <div class="col-md-6">
                                        <input-field label="State" type="text"
                                            v-model="certificate.certificateInfo.state"
                                            :validate="getValidation('State')" :editable="true" />
                                    </div>
                                    <div class="col-md-6">
                                        <input-field label="Locality" type="text"
                                            v-model="certificate.certificateInfo.locality"
                                            :validate="getValidation('Locality')" :editable="true" />
                                    </div>
                                </div>
                                <template v-else>
                                    <dl class="pairs">
                                        <div>
                                            <dt>
                                                Name
                                                <i class="fa fa-circle-info text-muted" title="Organization's name"
                                                    data-bs-toggle="tooltip" data-bs-placement="right"></i>
                                            </dt>
                                            <dd>{{ certificate.certificateInfo.organizationName || '—' }}</dd>
                                        </div>
                                        <div>
                                            <dt>
                                                Country
                                                <i class="fa fa-circle-info text-muted" title="Organization's country"
                                                    data-bs-toggle="tooltip" data-bs-placement="right"></i>
                                            </dt>
                                            <dd>{{ certificate.certificateInfo.country || '—' }}</dd>
                                        </div>
                                        <div>
                                            <dt>
                                                State
                                                <i class="fa fa-circle-info text-muted" title="Organization's state"
                                                    data-bs-toggle="tooltip" data-bs-placement="right"></i>
                                            </dt>
                                            <dd>{{ certificate.certificateInfo.state || '—' }}</dd>
                                        </div>
                                        <div>
                                            <dt>
                                                Locality
                                                <i class="fa fa-circle-info text-muted" title="Organization's locality"
                                                    data-bs-toggle="tooltip" data-bs-placement="right"></i>
                                            </dt>
                                            <dd>{{ certificate.certificateInfo.locality || '—' }}</dd>
                                        </div>
                                    </dl>
                                </template>
                            </div>
                            <div class="card-body" v-else>
                                <div class="sk" style="width:60%"></div>
                            </div>
                        </div>
                    </div>

                    <!-- Organization -->
                    <div class="col-md-6">
                        <div class="card" :class="{ 'card-editing': isEditing('org') }">
                            <div class="card-header cert-card-header">
                                <h5>Organization</h5>
                                <span v-if="isEditing('org')" class="badge-soft bs-info ms-auto">Editing</span>
                                <button v-else type="button" class="btn btn-sm btn-outline-primary ms-auto"
                                    @click="startEdit('org')" :disabled="!certificate?.org">Edit</button>
                            </div>
                            <div class="card-body" v-if="certificate?.org">
                                <template v-if="isEditing('org')">
                                    <div class="row g-3">
                                        <div class="col-12">
                                            <input-field label="Organization name" type="text"
                                                v-model="certificate.org.organizationName"
                                                :toolTip="`Organization's name`"
                                                :validate="getValidation(`Organization name`)" :editable="true" />
                                        </div>
                                        <div class="col-12">
                                            <input-field label="Unit" type="text" :toolTip="`Organization's unit`"
                                                v-model="certificate.org.organizationUnit" :editable="true" />
                                        </div>
                                        <div class="col-sm-4">
                                            <input-field label="Country" type="text" v-model="certificate.org.country"
                                                :validate="getValidation('Country')" :editable="true"
                                                :toolTip="`Organization's country`" />
                                        </div>
                                        <div class="col-sm-4">
                                            <input-field label="State" type="text" v-model="certificate.org.state"
                                                :validate="getValidation('State')" :editable="true"
                                                :toolTip="`Organization's state`" />
                                        </div>
                                        <div class="col-sm-4">
                                            <input-field label="Locality" type="text" v-model="certificate.org.locality"
                                                :validate="getValidation('Locality')" :editable="true"
                                                :toolTip="`Organization's locality`" />
                                        </div>
                                        <div class="col-12">
                                            <input-field label="Address line 1" type="text"
                                                v-model="certificate.org.addressLineOne"
                                                :validate="getValidation('Address line 1')" :editable="true"
                                                :toolTip="`Organization's address line 1`" />
                                        </div>
                                        <div class="col-12">
                                            <input-field label="Address line 2" type="text"
                                                v-model="certificate.org.addressLineTwo" :editable="true"
                                                :toolTip="`Organization's address line 2`" />
                                        </div>
                                        <div class="col-sm-6">
                                            <input-field label="Postal code" type="text"
                                                v-model="certificate.org.postalCode"
                                                :validate="getValidation('Postal code')" :editable="true"
                                                :toolTip="`Organization's postal code`" />
                                        </div>
                                        <div class="col-sm-6">
                                            <input-field label="Phone" type="text" v-model="certificate.org.phone"
                                                :validate="getValidation('Phone')" :editable="true"
                                                :toolTip="`Organization's phone`" />
                                        </div>
                                    </div>
                                </template>
                                <template v-else>
                                    <dl class="pairs">
                                        <div>
                                            <dt>
                                                Name
                                                <i class="fa fa-circle-info text-muted" title="Organization's name"
                                                    data-bs-toggle="tooltip" data-bs-placement="right"></i>
                                            </dt>
                                            <dd>{{ certificate.org.organizationName || '—' }}</dd>
                                        </div>
                                        <div>
                                            <dt>
                                                Unit
                                                <i class="fa fa-circle-info text-muted" title="Organization's unit"
                                                    data-bs-toggle="tooltip" data-bs-placement="right"></i>
                                            </dt>
                                            <dd>{{ certificate.org.organizationUnit || '—' }}</dd>
                                        </div>
                                        <div>
                                            <dt>
                                                Country
                                                <i class="fa fa-circle-info text-muted" title="Organization's country"
                                                    data-bs-toggle="tooltip" data-bs-placement="right"></i>
                                            </dt>
                                            <dd>{{ certificate.org.country || '—' }}</dd>
                                        </div>
                                        <div>
                                            <dt>
                                                State
                                                <i class="fa fa-circle-info text-muted" title="Organization's state"
                                                    data-bs-toggle="tooltip" data-bs-placement="right"></i>
                                            </dt>
                                            <dd>{{ certificate.org.state || '—' }}</dd>
                                        </div>
                                        <div>
                                            <dt>
                                                Locality
                                                <i class="fa fa-circle-info text-muted" title="Organization's locality"
                                                    data-bs-toggle="tooltip" data-bs-placement="right"></i>
                                            </dt>
                                            <dd>{{ certificate.org.locality || '—' }}</dd>
                                        </div>
                                        <div>
                                            <dt>
                                                Address Line 1
                                                <i class="fa fa-circle-info text-muted"
                                                    title="Organization's address line 1" data-bs-toggle="tooltip"
                                                    data-bs-placement="right"></i>
                                            </dt>
                                            <dd>{{ certificate.org.addressLineOne || '—' }}</dd>
                                        </div>
                                        <div>
                                            <dt>
                                                Address Line 2
                                                <i class="fa fa-circle-info text-muted"
                                                    title="Organization's address line 2" data-bs-toggle="tooltip"
                                                    data-bs-placement="right"></i>
                                            </dt>
                                            <dd>{{ certificate.org.addressLineTwo || '—' }}</dd>
                                        </div>
                                        <div>
                                            <dt>
                                                Postal code
                                                <i class="fa fa-circle-info text-muted"
                                                    title="Organization's postal code" data-bs-toggle="tooltip"
                                                    data-bs-placement="right"></i>
                                            </dt>
                                            <dd>{{ certificate.org.postalCode || '—' }}</dd>
                                        </div>
                                        <div>
                                            <dt>
                                                Phone
                                                <i class="fa fa-circle-info text-muted" title="Organization's phone"
                                                    data-bs-toggle="tooltip" data-bs-placement="right"></i>
                                            </dt>
                                            <dd>{{ certificate.org.phone || '—' }}</dd>
                                        </div>
                                    </dl>
                                </template>
                            </div>
                            <div class="card-body" v-else>
                                <div class="sk" style="width:65%"></div>
                                <div class="sk mt-2" style="width:50%"></div>
                                <div class="sk mt-2" style="width:80%"></div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Save bar, only while editing -->
                <div class="save-bar" v-if="editing.length">
                    <span class="save-bar-status">
                        <template v-if="saveBlockers.length">{{ saveBlockers.length }} field{{ saveBlockers.length === 1
                            ? '' : 's' }} need attention</template>
                        <template v-else-if="dirty">Unsaved changes</template>
                        <template v-else>No changes yet</template>
                    </span>
                    <div class="ms-auto d-flex gap-2">
                        <button type="button" class="btn btn-sm btn-light" @click="onCancelClick"
                            :disabled="saving">Discard</button>
                        <button type="button" class="btn btn-sm btn-primary" @click="onSaveClick"
                            :disabled="saving || !dirty || saveBlockers.length > 0"
                            :title="saveBlockers.length ? saveBlockers.join(', ') : null">
                            <span v-if="saving" class="spinner-border spinner-border-sm" role="status"
                                style="width: 1em; height: 1em;">
                                <span class="visually-hidden">Saving…</span>
                            </span>
                            <span v-else>Save changes</span>
                        </button>
                    </div>
                </div>
            </div>

            <div class="col-lg-4">
                <!-- Validity -->
                <div class="card">
                    <div class="card-header cert-card-header">
                        <h5>Validity</h5>
                    </div>
                    <div class="card-body">
                        <template v-if="expiryLoading">
                            <div class="sk sk-lg"></div>
                            <div class="sk mt-2" style="width:80%"></div>
                        </template>
                        <template v-else-if="expiryDate">
                            <div class="cert-metric">
                                <span class="days-left" :class="expiryTone">{{ daysLeftLabel }}</span>
                            </div>
                            <div class="cert-metric-sub">Expires {{ expiryDate }}</div>
                        </template>
                        <template v-else>
                            <div class="cert-metric text-muted">{{ expiry || 'Not available' }}</div>
                            <div class="cert-metric-sub">An expiry date exists once the certificate is deployed.</div>
                        </template>
                    </div>
                </div>

                <!-- Issuance and deployment -->
                <div class="card">
                    <div class="card-header cert-card-header">
                        <h5>Issuance &amp; deployment</h5>
                        <span v-if="certDeploy" class="badge-soft ms-auto" :class="railBadgeClass">{{ railBadge
                        }}</span>
                    </div>
                    <div class="card-body" v-if="certDeploy">
                        <ul class="rail">
                            <li v-for="step in steps" :key="step.key" :class="step.state">
                                <span class="rail-node"></span>
                                <div class="rail-step">{{ step.label }}</div>
                                <div class="rail-say" v-if="step.say">{{ step.say }}</div>
                                <div class="rail-do" v-if="step.actions">
                                    <button type="button" class="btn btn-sm btn-primary"
                                        @click="showModal({ component: 'GetCertificateCSR', data: certDeploy })">Get
                                        CSR</button>
                                    <button type="button" class="btn btn-sm btn-outline-secondary"
                                        @click="showModal({ component: 'UpdateSignedCertificate', data: certDeploy })">Upload
                                        signed
                                        certificate</button>
                                </div>
                            </li>
                        </ul>
                        <div class="freshness">
                            <span> Checked {{ checkedAgo }} </span>
                            <button type="button" class="btn btn-sm btn-light ms-auto" @click="onRefreshClick"
                                :disabled="readingDeployment">
                                <span v-if="readingDeployment" class="spinner-border spinner-border-sm"
                                    style="width: 1em; height: 1em;" role="status">
                                    <span class="visually-hidden">Refreshing…</span>
                                </span>
                                <span v-else>Refresh</span>
                            </button>
                        </div>
                    </div>
                    <div class="card-body" v-else-if="readingDeployment">
                        <div class="sk" style="width:55%"></div>
                        <div class="sk mt-3" style="width:70%"></div>
                        <div class="sk mt-3" style="width:45%"></div>
                    </div>
                    <div class="card-body text-center py-5" v-else>
                        <h5 class="mb-1">Deployment status is unavailable</h5>
                        <p class="text-muted small mb-3">The certificate itself is unaffected.</p>
                        <button type="button" class="btn btn-sm btn-outline-secondary" @click="onRefreshClick">Try
                            again</button>
                    </div>
                </div>

                <!-- Warnings -->
                <div class="card" v-if="certDeploy">
                    <div class="card-header cert-card-header">
                        <h5>Warnings</h5>
                        <span class="badge-soft ms-auto" :class="warningList.length ? 'bs-warn' : 'bs-ok'">
                            {{ warningList.length ? `${warningList.length} open` : 'None' }}
                        </span>
                    </div>
                    <div class="card-body" v-if="warningList.length">
                        <div class="warn-item" v-for="(warning, i) in warningList" :key="i">
                            <i class="fa fa-triangle-exclamation warn-ico"></i>
                            <div>
                                <div class="warn-title">{{ warning.title }}</div>
                                <div class="warn-detail" v-if="warning.detail">{{ warning.detail }}</div>
                            </div>
                        </div>
                        <div class="d-flex gap-2 mt-3 justify-content-end">
                            <button type="button" class="btn btn-sm btn-primary"
                                @click="showModal({ component: 'AcknowledgeDenyWarnings', data: certDeploy })">
                                Acknowledge or deny
                            </button>
                        </div>
                    </div>
                    <div class="card-body text-center py-4" v-else>
                        <p class="text-muted small mb-0">No warnings on this certificate.</p>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import dayjs from "dayjs";
import { mapActions } from 'vuex/dist/vuex.cjs.js';
import InputField from "../../components/InputField.vue";
import { deepEqual, relativeTime } from "../../utils/commonFunction";

// Akamai CPS change statuses, in the words an operator would use.
const STATUS_LABELS = {
    "wait-upload-third-party": "Awaiting your signed certificate",
    "verify-third-party-cert-request": "Verifying your signed certificate",
    "pre-verification-safety-checks": "Running safety checks",
    "wait-review-pre-verification-safety-checks": "Safety checks need review",
    "wait-review-third-party-cert": "Signed certificate needs review",
    "coodinate-domain-validation": "Validating domain ownership",
    "run-domain-validation": "Validating domain ownership",
    "wait-ack-change-management": "Awaiting change-management approval",
    "wait-review-cert-warning": "Certificate warnings need review",
    "complete": "Deployed to the edge",
};

// Renewal thresholds: inside these many days the validity reading turns amber,
// then red. They are the only scale the certificate is judged against.
const RENEW_LEAD_DAYS = 30;
const CRITICAL_DAYS = 7;

export default {
    components: { InputField },
    name: "Certificate Details",
    data() {
        return {
            certDeploy: null,
            certificate: null,
            snapshot: null,
            editing: [],
            reading: false,
            readingDeployment: false,
            saving: false,
            intervalCount: 0,
            intervalId: null,
            tickId: null,
            now: Date.now(),
            lastCheckedAt: null,
            copiedDomains: false,
            expiry: "",
            expiryLoading: false,
        }
    },
    computed: {
        // --- deployment ------------------------------------------------------
        deploymentState() {
            return this.certDeploy?.deployment_state;
        },
        deploymentStatus() {
            return this.certDeploy?.deployment_status;
        },
        statusText() {
            if (!this.deploymentStatus) return "";
            return STATUS_LABELS[this.deploymentStatus] || this.humanize(this.deploymentStatus);
        },
        awaitingUpload() {
            return this.deploymentState !== "cancelled" && this.deploymentStatus === "wait-upload-third-party";
        },
        // editLocked() {
        //     // Akamai rejects edits while a change is pending; only settled states are editable.
        //     return !!this.certDeploy && !["completed", "cancelled", "error"].includes(this.deploymentState);
        // },
        lockReason() {
            return "Locked while issuance is in progress. Abort changes to edit.";
        },
        steps() {
            const state = this.deploymentState;
            const done = { state: "done" };
            const requested = { key: "requested", label: "Request submitted", ...done };

            if (state === "completed") {
                return [
                    requested,
                    { key: "issuance", label: "Signed and validated", state: "done" },
                    { key: "deployed", label: "Deployed to the edge", state: "done", say: this.certDeploy?.description },
                ];
            }
            if (state === "cancelled") {
                return [
                    requested,
                    { key: "issuance", label: "Change aborted", state: "stopped", say: "No further steps will run for this change." },
                ];
            }
            if (state === "error") {
                return [
                    requested,
                    { key: "issuance", label: this.statusText || "Issuance failed", state: "fail", say: this.certDeploy?.description },
                    { key: "deployed", label: "Deployed to the edge", state: "todo" },
                ];
            }
            return [
                requested,
                {
                    key: "issuance",
                    label: this.statusText || "Validation and signing",
                    state: "active",
                    say: this.awaitingUpload
                        ? "Download the CSR, have your certificate authority sign it, then upload the result here."
                        : this.certDeploy?.description,
                    actions: this.awaitingUpload,
                },
                { key: "deployed", label: "Deployed to the edge", state: "todo" },
            ];
        },
        railBadge() {
            switch (this.deploymentState) {
                case "completed": return "Complete";
                case "cancelled": return "Aborted";
                case "error": return "Failed";
                default: return this.awaitingUpload ? "Action needed" : "In progress";
            }
        },
        railBadgeClass() {
            switch (this.deploymentState) {
                case "completed": return "bs-ok";
                case "error": return "bs-danger";
                case "cancelled": return "bs-muted";
                default: return this.awaitingUpload ? "bs-warn" : "bs-info";
            }
        },
        warningList() {
            const raw = this.certDeploy?.warnings;
            if (!raw) return [];
            if (Array.isArray(raw)) return raw.map(w => this.toWarning(w));
            if (typeof raw === "string") {
                return raw.split(/\r?\n/).map(l => l.trim()).filter(Boolean).map(l => this.toWarning(l));
            }
            return Object.entries(raw).map(([key, value]) => ({
                title: this.humanize(key),
                detail: typeof value === "string" ? value : JSON.stringify(value),
            }));
        },

        // --- expiry ----------------------------------------------------------
        expiryDate() {
            if (!this.expiry || this.expiry === "NA") return null;
            const parsed = dayjs(this.expiry);
            return parsed.isValid() ? parsed : null;
        },
        expiryDisplay() {
            return this.expiryDate ? this.expiryDate.format("D MMM YYYY") : "";
        },
        // Both of these read `now`, which ticks, so a page left open overnight
        // does not keep claiming yesterday's day count.
        expired() {
            return !!this.expiryDate && !this.expiryDate.isAfter(dayjs(this.now));
        },
        // Round up: six hours of validity is one day left, not zero.
        daysLeft() {
            return this.expiryDate ? Math.ceil(this.expiryDate.diff(dayjs(this.now), "hour") / 24) : 0;
        },
        daysLeftLabel() {
            if (this.expired) return "Expired";
            return `${this.daysLeft} ${this.daysLeft === 1 ? "day" : "days"} left`;
        },
        expiryTone() {
            if (this.expired || this.daysLeft <= CRITICAL_DAYS) return "crit";
            if (this.daysLeft <= RENEW_LEAD_DAYS) return "warn";
            return "ok";
        },

        // --- certificate content ---------------------------------------------
        sanList() {
            return this.certificate?.sans || [];
        },
        // The common name is usually repeated in the SANs, so count what the
        // certificate actually covers rather than how many times a name was listed.
        coveredDomains() {
            const seen = new Set();
            return [this.certificate?.commonName, ...this.sanList].reduce((names, name) => {
                const value = String(name ?? "").trim();
                const key = value.toLowerCase();
                if (value && !seen.has(key)) {
                    seen.add(key);
                    names.push(value);
                }
                return names;
            }, []);
        },
        adminName() {
            const admin = this.certificate?.admin || {};
            return [admin.firstName, admin.lastName].filter(Boolean).join(" ");
        },
        subjectDn() {
            const info = this.certificate?.certificateInfo || {};
            return [
                ["CN", this.certificate?.commonName],
                ["O", info.organizationName],
                ["L", info.locality],
                ["ST", info.state],
                ["C", info.country],
            ].filter(([, value]) => value).map(([key, value]) => `${key}=${value}`).join(", ");
        },
        subjectMatchesOrg() {
            const info = this.certificate?.certificateInfo || {};
            const org = this.certificate?.org || {};
            return ["organizationName", "country", "state", "locality"]
                .every(key => (info[key] || "") === (org[key] || ""));
        },

        // --- editing ----------------------------------------------------------
        dirty() {
            return !!this.snapshot && !deepEqual(this.certificate, this.snapshot);
        },
        saveBlockers() {
            if (!this.certificate) return [];
            const missing = [];
            const require = (label, value) => { if (!value || !String(value).trim()) missing.push(label); };

            // A certificate can legitimately cover only its common name, so SANs are
            // only required back if this certificate already had them.
            if (this.editing.includes("domains") && this.snapshot?.sans?.length && !this.sanList.length) {
                missing.push("Subject alternative names");
            }
            if (this.editing.includes("admin")) {
                const admin = this.certificate.admin || {};
                require("First name", admin.firstName);
                require("Last name", admin.lastName);
                require("Phone", admin.phone);
                if (!admin.email || !/^\S+@\S+\.\S+$/.test(admin.email)) missing.push("Email");
            }
            if (this.editing.includes("subject")) {
                const info = this.certificate.certificateInfo || {};
                require("Subject organization name", info.organizationName);
                require("Subject country", info.country);
                require("Subject state", info.state);
                require("Subject locality", info.locality);
            }
            if (this.editing.includes("org")) {
                const org = this.certificate.org || {};
                require("Organization name", org.organizationName);
                require("Country", org.country);
                require("State", org.state);
                require("Locality", org.locality);
                require("Address line 1", org.addressLineOne);
                require("Postal code", org.postalCode);
                require("Organization phone", org.phone);
            }
            return missing;
        },
        emailValidation() {
            return [
                { type: 'required', errMsg: 'Email is required' },
                { type: 'email', errMsg: 'Enter a complete email address, for example aut@aqfer.com' },
            ];
        },

        // --- freshness ---------------------------------------------------------
        checkedAgo() {
            if (!this.lastCheckedAt) return "just now";
            return relativeTime(this.now, this.lastCheckedAt) || "just now";
        },
    },
    methods: {
        ...mapActions({
            fetchCertificate: "aut/fetchCertificate",
            updateCertificate: "aut/updateCertificate",
            getDeploymentStatus: "aut/getCertificateDeploymentStatus",
            getCertificateExpiry: "aut/getCertificateExpiry",
            showModal: "showModal"
        }),
        humanize(value) {
            return String(value).replace(/[-_]+/g, " ").replace(/^./, c => c.toUpperCase());
        },
        toWarning(line) {
            const [title, ...rest] = String(line).split(":");
            return { title: title.trim(), detail: rest.join(":").trim() };
        },
        isEditing(section) {
            return this.editing.includes(section);
        },
        startEdit(section) {
            if (!this.editing.includes(section)) this.editing.push(section);
        },
        async copyDomains() {
            const all = this.coveredDomains.join("\n");
            try {
                await navigator.clipboard.writeText(all);
                this.copiedDomains = true;
                setTimeout(() => { this.copiedDomains = false; }, 2000);
            } catch (error) {
                this.$store.dispatch("addToast", { message: "Could not copy to the clipboard.", type: "error" });
            }
        },
        async readCertificate(forceGet) {
            let response = {};
            this.reading = true;
            if (forceGet || !this.$route.params.data) {
                try {
                    response = await this.fetchCertificate(this.$route.params.id);
                } catch (error) {
                    let err = null;
                    if (error.response?.data?.error) err = error.response.data.error;
                    else err = error.message;
                    let toastOptions = {
                        message: err || "Something went wrong while fetching certificate. Please contact support.",
                        type: "error"
                    }
                    console.error("Error in fetching certificate: ", err);
                    this.$store.dispatch("addToast", toastOptions);
                }
            } else {
                response["data"] = JSON.parse(this.$route.params.data);
            }
            this.certificate = await response.data;
            this.snapshot = JSON.parse(JSON.stringify(this.certificate ?? null)) // deep clone
            this.$emit("setCertificate", this.certificate);
            this.reading = false;
        },
        async readCertificateExpiry() {
            this.expiryLoading = true;
            this.expiry = "";
            await this.getCertificateExpiry(this.$route.params.id).then(response => {
                this.expiry = response.data?.expiry ? response.data.expiry : "NA";
            }).catch(error => {
                let err = null;
                if (error.response?.data?.error) err = error.response.data.error;
                else err = error.message;
                console.error("Error in fetching certificate expiry: ", err);
            })
            this.expiryLoading = false;
        },
        async readCertificateDeployment() {
            this.readingDeployment = true;
            await this.getDeploymentStatus(this.$route.params.id)
                .then(response => {
                    this.certDeploy = response.data;
                    this.lastCheckedAt = Date.now();
                    this.now = this.lastCheckedAt;
                    this.$emit("setCertDeploy", this.certDeploy);
                }).catch(error => {
                    let err = null;
                    if (error.response?.data?.error) err = error.response.data.error;
                    else err = error.message;
                    let toastOptions = {
                        message: err || "Something went wrong while fetching certificate deployment status. Please contact support.",
                        type: "error"
                    }
                    this.$store.dispatch("addToast", toastOptions);
                })
            this.readingDeployment = false;
        },
        startDeploymentPolling() {
            this.readCertificateDeployment(); // initial call
            this.readCertificateExpiry();

            if (this.intervalId) clearInterval(this.intervalId);
            this.intervalId = setInterval(() => {
                if (this.intervalCount >= 10) {
                    clearInterval(this.intervalId);
                    this.intervalId = null;
                    return;
                }
                this.readCertificateDeployment();
                this.readCertificateExpiry();
                this.intervalCount++;
            }, 10000); // update deployment status every 10s for 'ten' times
        },
        onCancelClick() {
            this.editing = [];
            this.certificate = JSON.parse(JSON.stringify(this.snapshot)); // restore
            this.$emit("setCertificate", this.certificate);
        },
        onRefreshClick() {
            this.readCertificateDeployment();
        },
        async onSaveClick() {
            this.saving = true;
            await this.updateCertificate(this.certificate)
                .then(() => {
                    this.editing = [];
                    this.readCertificate(true);
                    this.readCertificateExpiry();
                    this.$store.dispatch("addToast", { message: "Certificate updated.", type: "success" });
                }).catch(error => {
                    let err = null;
                    if (error.response?.data?.error) err = error.response.data.error;
                    else err = error.message;
                    let toastOptions = {
                        message: err || "Something went wrong while updating certificate. Please contact support.",
                        type: "error"
                    }
                    console.error("Error in update: ", err);
                    this.$store.dispatch("addToast", toastOptions);
                })
            this.saving = false;
        },
        copyFromOrg() {
            this.certificate.certificateInfo = {
                organizationName: this.certificate?.org?.organizationName || "",
                country: this.certificate?.org?.country || "",
                state: this.certificate?.org?.state || "",
                locality: this.certificate?.org?.locality || "",
            }
        },
        getValidation(name) {
            return [
                { type: 'required', errMsg: `${name} is required` }
            ]
        }
    },
    watch: {
        '$store.state.aut.getCertStatusTrigger': {
            handler() {
                this.readCertificateDeployment();
            },
            deep: true
        },
        '$store.state.aut.renewCertTrigger': {
            handler() {
                this.intervalCount = 0;
                this.readCertificate(true);
                this.readCertificateExpiry();
                this.startDeploymentPolling();
            },
            deep: true
        },
    },
    beforeMount() {
        this.readCertificate(false);
        this.readCertificateExpiry();
        this.startDeploymentPolling();
        this.tickId = setInterval(() => { this.now = Date.now(); }, 15000);
    },
    beforeUnmount() {
        if (this.intervalId) {
            clearInterval(this.intervalId);
        }
        if (this.tickId) {
            clearInterval(this.tickId);
        }
    },

    emits: ["setCertificate", "setCertDeploy"]
};
</script>

<style lang="scss" scoped>
@use '../../theme/scss/user-variables' as *;

/* InputField renders its own markup, so from this scoped block its internals
   are only reachable through :deep(). Anchoring every rule on .cert-page keeps
   the smaller type on this page and nowhere else. */
.cert-page :deep(.form-group) {
    margin-bottom: .5rem;
}

.cert-page :deep(.form-label),
.cert-page :deep(.col-form-label) {
    font-size: .8125rem;
}

.cert-page :deep(.form-control),
.cert-page :deep(.form-select) {
    font-size: .8125rem;
}

/* @vueform/multiselect sizes itself from custom properties. */
.cert-page :deep(.multiselect) {
    --ms-font-size: .8125rem;
    --ms-option-font-size: .8125rem;
    --ms-tag-font-size: .75rem;
}

.cert-page :deep(.invalid-feedback),
.cert-page :deep(.form-text) {
    font-size: .75rem;
}

/* Status band ------------------------------------------------------------ */
/* Equal thirds. The middle column used to be weighted for the validity meter;
   with that gone all three hold plain text and share the width evenly.
   minmax(0, 1fr) rather than 1fr so long domain lists wrap instead of
   stretching their column. */
/* Validity ---------------------------------------------------------------- */
.cert-metric {
    font-size: 1.0625rem;
    font-weight: 600;
    line-height: 1.3;
}

.cert-metric-sub {
    font-size: .8125rem;
    color: #6E84A3;
    margin-top: .3rem;
}

/* Urgency rides on the number itself — no meter. */
.days-left.warn {
    color: #C48B04;
}

.days-left.crit {
    color: #E63757;
}

/* Cards ------------------------------------------------------------------ */
/* Dashkit pins .card-header to a fixed 60px row, so a title and its actions
   spill out of the card as soon as they cannot share one line — which happens
   in the half-width columns. Let the header wrap and grow instead. */
.cert-card-header {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: .5rem .75rem;
    height: auto;
    min-height: 60px;
}

.cert-card-header h5 {
    margin: 0;
    font-size: .9375rem;
    font-weight: 600;
    min-width: 0;
    overflow-wrap: anywhere;
}

/* Badges and buttons wrap as one right-aligned group, never individually. */
.cert-card-actions {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: .5rem;
    margin-left: auto;
}

.card-editing {
    border-color: #BBD4F7;
    box-shadow: 0 0 0 2px rgba(44, 123, 229, .12);
}

.field-hint {
    font-size: .75rem;
    color: #95AAC9;
    margin-top: .35rem;
}

/* Domain chips ----------------------------------------------------------- */
.chips {
    display: flex;
    flex-wrap: wrap;
    gap: .4rem;
}

.chip {
    display: inline-flex;
    align-items: center;
    gap: .4rem;
    border: 1px solid #E3EBF6;
    background: #F9FBFD;
    border-radius: .3rem;
    padding: .25rem .55rem;
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
    font-size: .8125rem;
    color: #3B506C;
    word-break: break-all;
}

.chip-primary {
    border-color: #BBD4F7;
    background: #F0F6FE;
    color: #1A5FB8;
    font-weight: 600;
}

.chip-note {
    font-family: inherit;
    font-size: .6875rem;
    color: #2C7BE5;
    border-left: 1px solid #BBD4F7;
    padding-left: .4rem;
    font-weight: 400;
}

/* Read-mode pairs -------------------------------------------------------- */
.pairs {
    margin: 0;
}

/* "Common name" needs more room than "Name" or "Phone" do. */
.pairs-wide>div {
    grid-template-columns: 10rem 1fr !important;
}

.pairs>div {
    display: grid;
    grid-template-columns: 7rem 1fr;
    gap: 1rem;
    padding: .55rem 0;
    align-items: baseline;
}

.pairs>div+div {
    border-top: 1px solid #EDF2F9;
}

.pairs dt {
    font-weight: 400;
    color: #6E84A3;
    font-size: .8125rem;
    margin: 0;
}

.pairs dd {
    margin: 0;
    word-break: break-word;
    font-size: .8125rem;
}

/* Deployment rail -------------------------------------------------------- */
.rail {
    --rail-node: 1.1rem;
    --rail-top: .12rem;
    --rail-line: 2px;
    list-style: none;
    margin: 0;
    padding: 0;
}

.rail li {
    position: relative;
    padding: 0 0 1.35rem 1.95rem;
}

.rail li:last-child {
    padding-bottom: 0;
}

/* Track segment: leaves this node and stops short of the next one.
   Dashed while the work below it has not happened yet, solid once it has. */
.rail li::before {
    content: "";
    position: absolute;
    left: calc(var(--rail-node) / 2 - var(--rail-line) / 2);
    top: calc(var(--rail-top) + var(--rail-node) + .3rem);
    bottom: .25rem;
    width: var(--rail-line);
    border-radius: var(--rail-line);
    background-image: linear-gradient(#D2DDEC 55%, transparent 55%);
    background-size: var(--rail-line) .42rem;
    background-repeat: repeat-y;
}

.rail li:last-child::before {
    display: none;
}

.rail li.done::before {
    background-image: none;
    background-color: #00D97E;
}

/* The step in flight pulls the dashes along behind it. */
.rail li.active::before {
    background-image: linear-gradient(#2C7BE5 55%, transparent 55%);
    opacity: .55;
}

.rail li.fail::before,
.rail li.stopped::before {
    opacity: .6;
}

.rail-node {
    position: absolute;
    left: 0;
    top: var(--rail-top);
    width: var(--rail-node);
    height: var(--rail-node);
    box-sizing: border-box;
    border-radius: 50%;
    border: 2px solid #D2DDEC;
    background: #fff;
}

.rail-node::after {
    content: "";
    position: absolute;
    left: 50%;
    top: 50%;
}

.rail li.todo .rail-node {
    border-style: dashed;
    border-color: #D2DDEC;
    background: #F9FBFD;
}

/* Done: filled with a check drawn from two borders. */
.rail li.done .rail-node {
    border-color: #00D97E;
    background: #00D97E;
}

.rail li.done .rail-node::after {
    width: .2rem;
    height: .38rem;
    border: solid #fff;
    border-width: 0 2px 2px 0;
    transform: translate(-50%, -60%) rotate(45deg);
}

/* Active: open ring with a solid core, so it reads as "here, still running". */
.rail li.active .rail-node {
    border-width: 2px;
    border-color: #2C7BE5;
    background: #fff;
    box-shadow: 0 0 0 4px rgba(44, 123, 229, .16);
}

.rail li.active .rail-node::after {
    width: .36rem;
    height: .36rem;
    border-radius: 50%;
    background: #2C7BE5;
    transform: translate(-50%, -50%);
}

.rail li.fail .rail-node {
    border-color: #E63757;
    background: #E63757;
}

.rail li.fail .rail-node::after {
    width: 2px;
    height: .42rem;
    border-radius: 1px;
    background: #fff;
    box-shadow: 0 .21rem 0 -.045rem #fff;
    transform: translate(-50%, -62%);
}

.rail li.stopped .rail-node {
    border-color: #B1C2D9;
    background: #B1C2D9;
}

.rail li.stopped .rail-node::after {
    width: .38rem;
    height: 2px;
    border-radius: 1px;
    background: #fff;
    transform: translate(-50%, -50%);
}

.rail-step {
    font-weight: 600;
    font-size: .875rem;
    line-height: 1.35;
}

.rail li.todo .rail-step {
    color: #95AAC9;
    font-weight: 400;
}

.rail-say {
    font-size: .8125rem;
    color: #6E84A3;
    margin-top: .25rem;
}

.rail-do {
    margin-top: .6rem;
    display: flex;
    gap: .5rem;
    flex-wrap: wrap;
}

@media (prefers-reduced-motion: no-preference) {
    .rail li.active .rail-node {
        animation: cert-breathe 2.4s ease-in-out infinite;
    }

    .rail li.active::before {
        animation: cert-rail-flow 1.1s linear infinite;
    }

    @keyframes cert-breathe {

        0%,
        100% {
            box-shadow: 0 0 0 4px rgba(44, 123, 229, .16);
        }

        50% {
            box-shadow: 0 0 0 7px rgba(44, 123, 229, .06);
        }
    }

    @keyframes cert-rail-flow {
        from {
            background-position: 0 0;
        }

        to {
            background-position: 0 .42rem;
        }
    }
}

.freshness {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: .5rem;
    font-size: .75rem;
    color: #95AAC9;
    border-top: 1px solid #EDF2F9;
    margin-top: 1.1rem;
    padding-top: .85rem;
}

/* Warnings --------------------------------------------------------------- */
.warn-item {
    display: flex;
    gap: .75rem;
    padding: .75rem 0;
    border-top: 1px solid #EDF2F9;
}

.warn-item:first-of-type {
    border-top: none;
    padding-top: 0;
}

.warn-ico {
    color: #F6C343;
    margin-top: .15rem;
}

.warn-title {
    font-size: .875rem;
    font-weight: 500;
}

.warn-detail {
    font-size: .8125rem;
    color: #6E84A3;
    margin-top: .15rem;
    word-break: break-word;
}

/* Save bar --------------------------------------------------------------- */
.save-bar {
    position: sticky;
    bottom: 1rem;
    z-index: 5;
    background: rgba(255, 255, 255, .97);
    border: 1px solid #E3EBF6;
    border-radius: .5rem;
    padding: .7rem 1rem;
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: .75rem;
    box-shadow: 0 -.25rem 1rem rgba(18, 38, 63, .06);
    margin-bottom: 1.5rem;
}

.save-bar-status {
    font-size: .8125rem;
    color: #6E84A3;
}

/* Badges ----------------------------------------------------------------- */
.badge-soft {
    font-size: .6875rem;
    font-weight: 600;
    border-radius: .25rem;
    padding: .2rem .45rem;
    white-space: nowrap;
}

.bs-ok {
    background: #E4FAF1;
    color: #0B7A4B;
}

.bs-warn {
    background: #FEF6E0;
    color: #9A7212;
}

.bs-info {
    background: #EAF3FD;
    color: #1A5FB8;
}

.bs-danger {
    background: #FDE9EC;
    color: #A31B32;
}

.bs-muted {
    background: #EDF2F9;
    color: #6E84A3;
}

/* Skeletons -------------------------------------------------------------- */
.sk {
    display: block;
    height: .7rem;
    border-radius: .25rem;
    background: linear-gradient(90deg, #EDF2F9 25%, #F9FBFD 37%, #EDF2F9 63%);
    background-size: 400% 100%;
    animation: cert-sk 1.4s ease infinite;
}

.sk-lg {
    height: 1.1rem;
    width: 55%;
}

@keyframes cert-sk {
    0% {
        background-position: 100% 50%;
    }

    100% {
        background-position: 0 50%;
    }
}

@media (prefers-reduced-motion: reduce) {
    .sk {
        animation: none;
    }
}

/* Dark mode ------------------------------------------------------------- */
/* .card, .card-header and text colours come from the global dark theme in
   _user.scss; everything below is the surfaces this view adds itself. */
body.dark-mode {
    .cert-metric-sub {
        color: $dark-text-secondary;
    }

    .days-left.warn {
        color: #F6C343;
    }

    .days-left.crit {
        color: #FF6B81;
    }

    .field-hint {
        color: $dark-text-secondary;
    }

    .card-editing {
        border-color: rgba(44, 123, 229, .55);
        box-shadow: 0 0 0 2px rgba(44, 123, 229, .22);
    }

    .chip {
        background: $dark-input-bg;
        border-color: $dark-border-color;
        color: $dark-text-primary;
    }

    .pairs>div+div {
        border-top-color: $dark-border-color;
    }

    .pairs dt {
        color: $dark-text-secondary;
    }

    .dn-box {
        background: $dark-elevated-bg;
        border: 1px solid $dark-border-color;
        color: $dark-text-primary;
    }

    .rail li::before {
        background-image: linear-gradient(#4a525c 55%, transparent 55%);
        background-color: transparent;
    }

    .rail li.done::before {
        background-image: none;
        background-color: #00a854;
    }

    .rail li.active::before {
        background-image: linear-gradient(#4a8fe8 55%, transparent 55%);
    }

    .rail-node {
        border-color: #4a525c;
        background: $dark-card-bg;
    }

    .rail li.todo .rail-node {
        border-color: #3d434c;
        background: $dark-elevated-bg;
    }

    .rail li.done .rail-node {
        border-color: #00a854;
        background: #00a854;
    }

    .rail li.active .rail-node {
        border-color: #4a8fe8;
        background: $dark-card-bg;
    }

    .rail li.active .rail-node::after {
        background: #4a8fe8;
    }

    .rail li.stopped .rail-node {
        border-color: #6b7684;
        background: #6b7684;
    }

    .rail li.todo .rail-step,
    .rail-say {
        color: $dark-text-secondary;
    }

    .freshness {
        border-top-color: $dark-border-color;
        color: $dark-text-secondary;
    }

    .warn-item {
        border-top-color: $dark-border-color;
    }

    .warn-detail {
        color: $dark-text-secondary;
    }

    .save-bar {
        background: rgba($dark-card-bg, .97);
        border-color: $dark-border-color;
        box-shadow: 0 -.25rem 1rem rgba(0, 0, 0, .5);
    }

    .save-bar-status {
        color: $dark-text-secondary;
    }

    /* Soft badges: translucent tints read better than light washes on dark */
    .bs-ok {
        background: rgba(0, 217, 126, .16);
        color: #4ade8a;
    }

    .bs-warn {
        background: rgba(246, 195, 67, .16);
        color: #f3ca63;
    }

    .bs-info {
        background: rgba(44, 123, 229, .18);
        color: #7eb5ff;
    }

    .bs-danger {
        background: rgba(230, 55, 87, .18);
        color: #f28a9c;
    }

    .bs-muted {
        background: $dark-input-bg;
        color: $dark-text-secondary;
    }

    .sk {
        background: linear-gradient(90deg, $dark-input-bg 25%, $dark-elevated-bg 37%, $dark-input-bg 63%);
        background-size: 400% 100%;
    }
}
</style>
